import { Button, Dropdown, Menu, Tag } from "antd";
import clsx from "clsx";
import { cloneElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlinePlus } from "react-icons/ai";

import { capitalize, stopPropagation } from "shell/api/utils";

import type { FiltersMenuProps, FiltersSize } from "../types";

import s from "./FiltersMenu.module.css";

const getClassname = (size: FiltersSize = "middle") => {
  return {
    [s.small]: size === "small",
    [s.middle]: size === "middle",
    [s.large]: size === "large",
  };
};

export const FiltersMenu = ({ filters }: { filters: FiltersMenuProps }) => {
  const { t } = useTranslation(["common"]);
  const [isFiltersDropdownOpen, setIsFiltersDropdownOpen] = useState(false);
  const { resetAll, items, menuProps } = filters;
  const [renderKey, setRenderKey] = useState(0);

  const activeFilters = items.filter(f => f.values && f.values.length > 0);

  const handleFilterApplied = () => {
    setIsFiltersDropdownOpen(false);
    setRenderKey(prev => prev + 1);
  };

  const classNames = getClassname(menuProps?.filtersSize);

  const menu = (
    <Menu
      data-testid="filters-dropdown"
      mode="vertical"
      items={items.map(filter => ({
        key: filter.propertyToFilterBy,
        children: [],
        label: (
          <Dropdown
            trigger={["click"]}
            placement="topRight"
            getPopupContainer={() =>
              document.querySelector(
                `.${s.customFiltersContainer}`,
              ) as HTMLElement
            }
            popupRender={() => {
              return (
                <div
                  key={renderKey}
                  className={s.dropdownContent}
                  data-testid={`${filter?.testId}-filter-dropdown-content`}
                >
                  {filter.component &&
                    cloneElement(filter.component, {
                      onFilterApplied: handleFilterApplied,
                    })}
                </div>
              );
            }}
          >
            <div className={s.filterLabel} data-testid={filter?.testId}>
              {filter.label}
            </div>
          </Dropdown>
        ),
      }))}
    />
  );

  return (
    <section className={s.customFiltersContainer} data-testid="filters-menu">
      <div className={s.addFilterWrapper}>
        <Dropdown
          key={renderKey}
          className={s.filtersDropdown}
          open={isFiltersDropdownOpen}
          onOpenChange={setIsFiltersDropdownOpen}
          popupRender={() => menu}
          trigger={["click"]}
          placement="bottomLeft"
          getPopupContainer={() =>
            document.querySelector(
              `.${s.customFiltersContainer}`,
            ) as HTMLElement
          }
        >
          <Button
            type="dashed"
            size={menuProps?.filtersSize ?? "middle"}
            icon={<AiOutlinePlus />}
            data-testid="add-filter-button"
          >
            {menuProps?.addFilterButtonText ??
              t("components.buttons.addFilter")}
          </Button>
        </Dropdown>
      </div>

      {activeFilters.map(filter => {
        let labelText = filter.activeFilterLabel || "";

        if (!labelText) {
          const { values = [] } = filter;
          const shownValues = values.slice(0, 2);
          const remainingCount = values.length - shownValues.length;

          labelText =
            values.length > 0
              ? `${filter.label}: ${shownValues.map(v => capitalize(v.label)).join(", ")}${
                  remainingCount > 0
                    ? `, ${remainingCount} ${t("common:labels.more")}`
                    : ""
                }`
              : filter.label;
        }

        return (
          <Tag
            key={filter.propertyToFilterBy}
            closable
            onClose={e => {
              stopPropagation(e);
              filter.onReset();
            }}
            variant="outlined"
            className={clsx(s.filterTag, classNames)}
            data-testid={`${filter?.testId}-applied-tag`}
          >
            {labelText}
          </Tag>
        );
      })}

      {activeFilters.length > 0 && resetAll && (
        <Button
          type="link"
          size={menuProps?.filtersSize ?? "middle"}
          onClick={resetAll}
          data-testid="reset-all-filters-button"
        >
          {t("components.dataTable.buttons.resetFilters")}
        </Button>
      )}
    </section>
  );
};
